import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useProfiles } from '../../../../../../hooks/profile/useProfiles'

import { TextInput } from '../../../../../input/TextInput'
import { Tabs } from '../../../../../tabs/Tabs'
import { PhoneNumberInput } from '../../../../../input/phoneNumberInput/PhoneNumberInput'
import { SelectOptionInput } from '../../../../../input/selectOptionInput/SelectOptionInput'

import { FormField } from '../../../../../../types/formData'
import { ProfileMetadata } from '../../../../../../types/user/userProfile'
import { SortDirectionType, SortType } from '../../../../../../types/listing'
import {
  BookingRecipient,
  BookingRecipientType,
} from '../../../../../../types/court/court'
import { InputType } from '../../../../../../constants/inputTypes'
import { DEFAULT_PAGE_SIZE } from '../../../../../../constants/listing'

type NewProfileData = {
  firstName: FormField<string>
  lastName: FormField<string>
  email: FormField<string>
  phoneNumber: FormField<string>
}

const INIT_NEW_PROFILE: NewProfileData = {
  firstName: { value: '', isValid: true },
  lastName: { value: '', isValid: true },
  email: { value: '', isValid: true },
  phoneNumber: { value: '', isValid: true },
}

const PROFILE_SEARCH_DEBOUNCE = 300

const withoutDuplicateProfiles = (profiles: ProfileMetadata[]) =>
  Array.from(new Map(profiles.map(profile => [profile.id, profile])).values())

const getProfileLabel = (profile: ProfileMetadata) => {
  const fullName = [profile.firstName, profile.lastName]
    .map(value => value?.trim())
    .filter(Boolean)
    .join(' ')

  return fullName || profile.email?.trim() || profile.phoneNumber?.trim() || '—'
}

type BookingRecipientFormProps = {
  onChange: (recipient?: BookingRecipient) => void
  initialRecipient?: BookingRecipient
}

const BookingRecipientForm = ({
  onChange,
  initialRecipient,
}: BookingRecipientFormProps) => {
  const { t } = useTranslation('bookings')
  const [selectedProfileId, setSelectedProfileId] = useState(
    initialRecipient?.type === BookingRecipientType.EXISTING_PROFILE
      ? initialRecipient.profileId
      : ''
  )
  const [profiles, setProfiles] = useState<ProfileMetadata[]>([])
  const [profilePage, setProfilePage] = useState(0)
  const [profilesLastPage, setProfilesLastPage] = useState(false)
  const [profileSearch, setProfileSearch] = useState('')
  const [newProfile, setNewProfile] = useState<NewProfileData>(() =>
    initialRecipient?.type === BookingRecipientType.NEW_PROFILE
      ? {
          firstName: { value: initialRecipient.firstName ?? '', isValid: true },
          lastName: { value: initialRecipient.lastName ?? '', isValid: true },
          email: { value: initialRecipient.email ?? '', isValid: true },
          phoneNumber: {
            value: initialRecipient.phoneNumber ?? '',
            isValid: true,
          },
        }
      : INIT_NEW_PROFILE
  )
  const [recipientMode, setRecipientMode] = useState<string>(
    initialRecipient?.type ?? BookingRecipientType.EXISTING_PROFILE
  )

  const { profiles: fetchProfiles, loading: profilesLoading } = useProfiles()
  const profileSearchRef = useRef(profileSearch)
  const searchRequestIdRef = useRef(0)
  const loadingMoreRef = useRef(false)

  useEffect(() => {
    const requestId = ++searchRequestIdRef.current
    const delay = profileSearch ? PROFILE_SEARCH_DEBOUNCE : 0
    const timeout = window.setTimeout(async () => {
      const response = await fetchProfiles(
        0,
        DEFAULT_PAGE_SIZE,
        SortType.NAME,
        SortDirectionType.ASC,
        profileSearch
      )

      if (requestId !== searchRequestIdRef.current || !response) {
        return
      }

      setProfiles(currentProfiles => {
        const selectedProfile = currentProfiles.find(
          profile => profile.id === selectedProfileId
        )
        const nextProfiles = selectedProfile
          ? [selectedProfile, ...response.content]
          : response.content

        return withoutDuplicateProfiles(nextProfiles)
      })
      setProfilePage(response.pageable.pageNumber)
      setProfilesLastPage(response.pageable.last)
    }, delay)

    return () => {
      window.clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileSearch])

  const handleProfileSearch = (value: string) => {
    profileSearchRef.current = value
    setProfilesLastPage(false)
    setProfileSearch(value)
  }

  const handleLoadMoreProfiles = async () => {
    if (loadingMoreRef.current || profilesLoading || profilesLastPage) {
      return
    }

    loadingMoreRef.current = true
    const searchAtRequest = profileSearchRef.current

    try {
      const response = await fetchProfiles(
        profilePage + 1,
        DEFAULT_PAGE_SIZE,
        SortType.NAME,
        SortDirectionType.ASC,
        searchAtRequest
      )

      if (!response || profileSearchRef.current !== searchAtRequest) {
        return
      }

      setProfiles(currentProfiles =>
        withoutDuplicateProfiles([...currentProfiles, ...response.content])
      )
      setProfilePage(response.pageable.pageNumber)
      setProfilesLastPage(response.pageable.last)
    } finally {
      loadingMoreRef.current = false
    }
  }

  const selectedProfile = profiles.find(
    profile => profile.id === selectedProfileId
  )

  const isNewProfile = recipientMode === BookingRecipientType.NEW_PROFILE

  const hasEmail = Boolean(newProfile.email.value.trim())
  const hasPhoneNumber = Boolean(newProfile.phoneNumber.value.trim())
  const hasValidContact =
    (hasEmail && newProfile.email.isValid) ||
    (hasPhoneNumber && newProfile.phoneNumber.isValid)
  const optionalContactFieldsAreValid =
    (!hasEmail || newProfile.email.isValid) &&
    (!hasPhoneNumber || newProfile.phoneNumber.isValid)
  const optionalNameFieldsAreValid =
    newProfile.firstName.isValid && newProfile.lastName.isValid

  const recipientValid =
    (recipientMode === BookingRecipientType.EXISTING_PROFILE &&
      Boolean(selectedProfile)) ||
    (isNewProfile &&
      hasValidContact &&
      optionalContactFieldsAreValid &&
      optionalNameFieldsAreValid)

  const recipientOptions = profiles.map(profile => ({
    value: profile.id,
    label: getProfileLabel(profile),
    searchText: [profile.email, profile.phoneNumber].filter(Boolean).join(' '),
  }))

  const recipientModeOptions = [
    {
      value: BookingRecipientType.EXISTING_PROFILE,
      label: t('booking.booking-courts.summary-panel.existing-user'),
    },
    {
      value: BookingRecipientType.NEW_PROFILE,
      label: t('booking.booking-courts.summary-panel.new-user-short'),
    },
  ]

  useEffect(() => {
    if (selectedProfile) {
      onChange({
        type: BookingRecipientType.EXISTING_PROFILE,
        profileId: selectedProfile.id,
      })
      return
    }

    if (
      initialRecipient?.type === BookingRecipientType.EXISTING_PROFILE &&
      selectedProfileId === initialRecipient.profileId
    ) {
      onChange(initialRecipient)
      return
    }

    if (isNewProfile && recipientValid) {
      onChange({
        type: BookingRecipientType.NEW_PROFILE,
        firstName: newProfile.firstName.value.trim() || undefined,
        lastName: newProfile.lastName.value.trim() || undefined,
        email: newProfile.email.value.trim() || undefined,
        phoneNumber: newProfile.phoneNumber.value.trim() || undefined,
      })
      return
    }

    onChange(undefined)
  }, [
    initialRecipient,
    isNewProfile,
    newProfile,
    onChange,
    recipientValid,
    selectedProfile,
    selectedProfileId,
  ])

  const handleNewProfileChange = (
    name: string,
    value: string,
    isValid: boolean
  ) => {
    const fieldName = name as keyof NewProfileData
    setNewProfile(current => ({
      ...current,
      [fieldName]: { value, isValid },
    }))
  }

  return (
    <div className="booking-recipient-form">
      <div className="header flex-sm-row align-items-sm-center">
        <div className="header-content">
          <div className="title">
            {t('booking.booking-courts.summary-panel.recipient.title')}
          </div>
          <div className="description">
            {t('booking.booking-courts.summary-panel.recipient.description')}
          </div>
        </div>
        <Tabs
          tab={recipientMode}
          tabs={recipientModeOptions}
          onChange={value => {
            setRecipientMode(value)
            setSelectedProfileId('')
          }}
          className="recipient-filter align-self-sm-auto"
        />
      </div>

      <div className="details">
        {isNewProfile ? (
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <TextInput
                name="firstName"
                value={newProfile.firstName.value}
                label={t(
                  'booking.booking-courts.summary-panel.new-user.first-name'
                )}
                onChange={handleNewProfileChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextInput
                name="lastName"
                value={newProfile.lastName.value}
                label={t(
                  'booking.booking-courts.summary-panel.new-user.last-name'
                )}
                onChange={handleNewProfileChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <TextInput
                name="email"
                type={InputType.Email}
                value={newProfile.email.value}
                label={t('booking.booking-courts.summary-panel.new-user.email')}
                onChange={handleNewProfileChange}
              />
            </div>
            <div className="col-12 col-md-6">
              <PhoneNumberInput
                name="phoneNumber"
                value={newProfile.phoneNumber.value}
                label={t(
                  'booking.booking-courts.summary-panel.new-user.phone-number'
                )}
                onChange={handleNewProfileChange}
              />
            </div>
          </div>
        ) : (
          <div className="col-12">
            <SelectOptionInput
              name="profileId"
              value={selectedProfileId}
              disabled={profilesLoading && profiles.length === 0}
              loading={profilesLoading}
              list={recipientOptions}
              onLoadMore={() => void handleLoadMoreProfiles()}
              onSearch={handleProfileSearch}
              onChange={(_, value) => setSelectedProfileId(value)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default BookingRecipientForm
